import { BuildRounded, CloudSyncRounded, FastForwardRounded, StarRounded, StoreRounded, AutorenewRounded, HealthAndSafety, MoveDownRounded, CarRental, KeyOff } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import * as React from "react";
import { ColDef, ColumnMovedEvent, ColumnVisibleEvent, GridReadyEvent, ICellRendererParams, IServerSideDatasource, SideBarDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation } from "@tanstack/react-query";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import { AgGridCustomToolbarProps } from "../../../../../../util/component/AgGridCustomToolbar";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { getBookings, getBookingsSummary, IGetBookingsReturn, IGetBookingsSummaryReturn } from "../../../../api/services";
import { ISortBy } from "../../../../api/types";
import { IBooking, VipType } from "../../../../types";
import { bookingsFilterState, summaryState } from "../../recoil";
import CellActions from "./cell-actions/CellActions";
import CellStatus from "./cell-status/CellStatus";
import ToolbarActions from "./toolbar/ToolbarActions";
import ToolbarInfo from "./toolbar/ToolbarInfo";
import CustomLoader from "../../../../../../util/component/CustomLoader";

const TableBookings = () => {

    const { t } = useTranslation('booking');

    const { globalData } = useContext(GlobalContext);


    const bookingsFilter = useRecoilValue(bookingsFilterState);
    const setSummary = useSetRecoilState(summaryState);

    const bookingListMutation = useMutation(getBookings);
    const bookingSummaryListMutation = useMutation(getBookingsSummary);

    const columnStateStorageKey = useMemo(() => {
        return `__BOOKING_SEARCH.tableColumsConfig:${globalData.user.username}`;
    }, [globalData.user.username]);
    const columnDefs = useMemo<ColDef<IBooking>[]>(() => [
        {
            field: '_actions',
            headerName: '',
            minWidth: 70,
            resizable: false,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            pinned: 'right',
            cellRenderer: CellActions,
        },
        {
            field: 'tags',
            headerName: t('label.tags'),
            pinned: 'left',
            suppressMovable: true,
            width: 100,
            suppressSizeToFit: true,
            suppressColumnsToolPanel: true,
            resizable: false,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<IBooking>) => {
                return (
                    <Stack direction="row" alignItems="center" columnGap={1}>
                        {params.data?.customerAtDesk &&
                            <StoreRounded
                                titleAccess={t('label.customerAtDesk')}
                                color="secondary"
                            />
                        }
                        {params.data && [VipType.Internal, VipType.External, VipType.VipCS].includes(params.data.vip) &&
                            <StarRounded
                                titleAccess={t('label.vip')}
                                color="secondary"
                            />
                        }
                        {params.data?.fastLine &&
                            <FastForwardRounded
                                titleAccess={t('label.fastLine')}
                                color="secondary"
                            />
                        }
                        {params.data?.carSwap &&
                            <CloudSyncRounded
                                titleAccess={t('label.carSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.substitution &&
                            <BuildRounded
                                titleAccess={t('label.substitution')}
                                color="secondary"
                            />
                        }
                        {params.data?.renewal &&
                            <AutorenewRounded
                                titleAccess={t('label.renewal')}
                                color="secondary"
                            />
                        }
                        {params.data?.recovery &&
                            <HealthAndSafety
                                titleAccess={t('label.recovery')}
                                color="secondary"
                            />
                        }
                        {params.data?.packageSwap &&
                            <MoveDownRounded
                                titleAccess={t('label.packageSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.keybox &&
                            <CarRental
                                titleAccess={t('label.keyBox')}
                                color="secondary"
                            />
                        }
                        {params.data?.keyless &&
                            <KeyOff
                                titleAccess={t('label.keyless')}
                                color="secondary"
                            />
                        }
                    </Stack>
                );
            }
        },
        {
            field: 'statusSubstatus',
            headerName: t('label.status'),
            pinned: 'left',
            maxWidth: 200,
            minWidth: 200,
            suppressColumnsToolPanel: true,
            suppressMovable: true,
            sortable: true,
            resizable: false,
            cellRenderer: CellStatus
        },
        {
            field: 'requestCreationDate',
            headerName: t('label.planetCreationDate'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => (
                dayjs(params.value).format('DD-MM-YYYY HH:mm')
            )
        },
        {
            field: 'id',
            headerName: t('label.bookingNumber'),
            minWidth: 200,
            sortable: true
        },
        {
            field: 'dossierNumber',
            headerName: t('label.dossierNumber'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IBooking>) => params.data?.dossierNumber || '-'
        },
        {
            field: 'acriss',
            headerName: t('label.acriss'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.carGroup.acrissCode || '-'
        },
        {
            field: 'sipp',
            headerName: t('label.sipp'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.carGroup.sippCode || '-'
        },
        {
            field: 'segment',
            headerName: t('label.segment'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IBooking>) => params.data?.segmentLabel || '-'
        },
        {
            field: 'category',
            headerName: t('label.category'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IBooking>) => params.data?.categoryLabel || '-'
        },
        {
            field: 'idSmProduct',
            headerName: t('label.product'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IBooking>) => params.data?.idSmProductLabel || '-'
        },
        {
            field: 'rate',
            headerName: t('label.rate'),
            minWidth: 200,
            sortable: true,
            valueGetter: (params: ValueGetterParams<IBooking>) => params.data?.rateLabel || '-'
        },
        {
            field: 'substitution',
            headerName: t('label.substitutionRequest'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, boolean>) => params.value ? t('label.yes') : t('label.no')
        },
        {
            field: 'pickupLocation',
            headerName: t('label.pickupLocation'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.pickupLocation.label || '-'
        },
        {
            field: 'pickupDateTime',
            headerName: t('label.pickupDateTime'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data ? dayjs(params.data.pickupDateLocalISO).format('DD-MM-YYYY HH:mm') : '-'
        },
        {
            field: 'dropoffLocation',
            headerName: t('label.dropoffLocation'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.dropoffLocation.label || '-'
        },
        {
            field: 'dropoffDateTime',
            headerName: t('label.dropoffDateTime'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data ? dayjs(params.data.dropoffDateLocalISO).format('DD-MM-YYYY HH:mm') : '-'
        },
        {
            field: 'totalRentalDays',
            headerName: t('label.rentalDays'),
            minWidth: 200,
            sortable: true
        },
        {
            field: 'customerCode',
            headerName: t('label.businessAgreement'),
            minWidth: 200,
            sortable: true
        },
        {
            field: 'bookingOwner',
            headerName: t('label.bookingOwner'),
            minWidth: 200,
            valueFormatter: (params: ValueFormatterParams<IBooking>) =>
                params.data?.bookingOwner ?
                    `${params.data.bookingOwner.surname} ${params.data.bookingOwner.name}` :
                    params.data?.bookingCompanyOwner ?
                        params.data.bookingCompanyOwner.companyName :
                        '-'
        },
        {
            field: 'driver',
            headerName: t('label.mainDriver'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.firstDriver ? `${params.data.firstDriver.surname} ${params.data.firstDriver.name}` : '-'
        },
        {
            field: 'vip',
            headerName: t('label.vip'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => t(`vip_type.${params.data?.vip}`)
        },
        // {
        //     field: 'keyless',
        //     headerName: t('label.keyless'),
        //     minWidth: 200,
        //     sortable: true,
        //     cellRenderer: (params: any) => {
        //         return (<span>{t(params.booking?.keyless ? 'label.yes' : 'label.no')}</span>)
        //     }
        // },
        {
            field: 'rentwayNumber',
            headerName: t('label.rentwayNumber'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => params.value || '-'
        },
        {
            field: 'vehicleType',
            headerName: t('label.vehicleType'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => t(`vehicle_type.${params.data?.carGroup.bookedVehicleType}`)
        },
        {
            field: 'assignedPlateNumber',
            headerName: t('label.licensePlateNumber'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => params.value || '-'
        },
        {
            field: 'subscriptionNumber',
            headerName: t('label.subscriptionNumber'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => params.value || '-'
        },
        {
            field: 'renewal',
            headerName: t('label.renewal'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, boolean>) => params.value ? t('label.yes') : t('label.no')
        },
        {
            field: 'createdBy',
            headerName: t('label.createdBy'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking>) => params.data?.creationOperator ? params.data.creationOperator.username : '-'
        },
        {
            field: 'affiliationCode',
            headerName: t('label.affiliationCode'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => params.value || '-'
        },
        {
            field: 'salesChannel',
            headerName: t('label.salesChannel'),
            minWidth: 200,
            sortable: true,
            valueFormatter: (params: ValueFormatterParams<IBooking, string>) => params.value || '-'
        },
        {
            field: 'ancillariesIncluded',
            headerName: t('label.ancillaries'),
            minWidth: 200,
            autoHeight: true,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<IBooking>) => {
                return (
                    <Box py={1}>
                        {params.data?.ancillaries
                            .filter(a => a.selected)
                            .map((a: any, i: number) => (
                                <Stack key={i} direction="row" alignItems="center" height={26}>
                                    { a.units > 1 && <>{a.units} </> }
                                    {a.label}
                                </Stack>
                            ))}
                    </Box>
                )
            },
        }
    ], [t]);
    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: true,
        lockPinned: true,
        unSortIcon: true
    }), []);
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {

                if(bookingsFilter == undefined) { 
                    
                    params.success({rowCount: 0, rowData: [] });
                    params.api.showNoRowsOverlay();
                    
                    return; 
                }
                const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                field: params.request.sortModel[0].colId,
                order: params.request.sortModel[0].sort
                } : undefined;
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

                let summary: number;
                bookingSummaryListMutation.mutateAsync({
                    filters: bookingsFilter
                }).then((response: IGetBookingsSummaryReturn) => {
                    setSummary(response.summary);
                    summary = response.summary.total;

                    bookingListMutation.mutateAsync({
                        filters: bookingsFilter,
                        sortBy: sortBy,
                        start: start,
                        limit: limit
                    }).then((response: IGetBookingsReturn) => {
                            params.success({
                                rowData: response.rows.map(r => r.booking),
                                rowCount: summary ?? 0
                            });

                            if (summary === 0) {
                                params.api.showNoRowsOverlay();
                            }
                    }).catch(() => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }).catch(() => {
                    params.fail();
                    params.api.hideOverlay();
                });
            } catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [bookingsFilter])
    const sideBar = useMemo<SideBarDef>(() => ({
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                    suppressColumnExpandAll: true,
                },
            },
        ],
        defaultToolPanel: '',
    }), []);
    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderInfo: ToolbarInfo,
        renderActions: ToolbarActions
    }), []);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });
    }, [columnStateStorageKey]);
    const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);
    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource={serverSideDatasource}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                paginationPageSize={25}
                cacheBlockSize={25}
                onGridReady={onGridReady}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
                sideBar={sideBar}
                toolbar={toolbar}
                suppressDragLeaveHidesColumns
                suppressMultiSort
                enableCellTextSelection
            />
            <CustomLoader
                loading={bookingListMutation.isLoading ||
                bookingSummaryListMutation.isLoading} />
        </>
    );

}

export default TableBookings;
